import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, Sparkles } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import heroConcert from "@/assets/hero-concert.jpg";
import FloatingParticles from "./FloatingParticles";
import AudioEqualizer from "./AudioEqualizer";
import { supabase } from "@/integrations/supabase/client";

const HeroSignup = () => {
  const [heroEmail, setHeroEmail] = useState("");
  const navigate = useNavigate();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const { scrollY } = useScroll();
  const fireX = useTransform(scrollY, [0, 800], [0, -1200]);
  

  return (
    <>
    <section ref={ref} className="relative min-h-[85vh] flex items-center overflow-hidden">
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={heroConcert}
          alt="Live concert crowd with stage lights"
          width={1920}
          height={1080}
          className="h-[120%] w-full object-cover"
        />
      </motion.div>

      {/* Rich layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />

      <FloatingParticles />

      {/* Animated spotlight sweep */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 500px 700px at var(--x) var(--y), hsl(25 95% 53% / 0.1), transparent)",
        }}
        animate={{
          "--x": ["20%", "80%", "50%", "20%"],
          "--y": ["30%", "50%", "70%", "30%"],
        } as any}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        className="container relative z-10 mx-auto px-4 pt-28 pb-20"
      >
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 glass px-4 py-2 text-sm text-primary"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-display font-medium">Relive Every Moment</span>
            <AudioEqualizer className="ml-1 h-4" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 font-display text-5xl font-bold leading-[1.1] tracking-tight text-foreground md:text-7xl lg:text-8xl"
          >
            Your Concert.
            <br />
            <motion.span
              className="text-gradient inline-block"
              animate={{
                filter: [
                  "drop-shadow(0 0 20px hsl(25 95% 53% / 0.3))",
                  "drop-shadow(0 0 40px hsl(25 95% 53% / 0.5))",
                  "drop-shadow(0 0 20px hsl(25 95% 53% / 0.3))",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              Your Encore.
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mb-8 max-w-lg text-lg text-muted-foreground leading-relaxed"
          >
            Share your best concert moments, discover incredible fan footage, and build your ultimate concert profile.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Input
              type="email"
              placeholder="Enter your email"
              value={heroEmail}
              onChange={(e) => setHeroEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/auth?email=${encodeURIComponent(heroEmail)}&tab=signup`);
              }}
              className="h-12 w-full glass border-border/50 text-foreground placeholder:text-muted-foreground sm:w-72 rounded-full text-base px-5"
            />
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button
                size="lg"
                className="h-12 gradient-hero border-0 font-display font-semibold px-8 shadow-lg shadow-primary/25 rounded-full text-base"
                onClick={() => navigate(`/auth?email=${encodeURIComponent(heroEmail)}&tab=signup`)}
              >
                <Play className="mr-2 h-4 w-4" />
                Get Started Free
              </Button>
            </motion.div>
          </motion.div>

        </div>
      </motion.div>
    </section>

    {/* Fire emoji marquee */}
    <div className="overflow-hidden bg-background py-2">
      <motion.div
        className="whitespace-nowrap text-2xl sm:text-3xl select-none"
        style={{ x: fireX }}
      >
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className="mx-1">🔥</span>
        ))}
      </motion.div>
    </div>
    </>
  );
};

export default HeroSignup;
